import { fireAdobeAnalyticsEvent } from './adobe-analytics';

// Activity gaps longer than this end an engaged stretch.
const IDLE_SECONDS = 30;
const MOVE_THROTTLE_MS = 1000;

// Viewer action names from the Bitreel contracts' view-changed events.
const VIEW_ACTIONS = {
  orbit: 'Orbit',
  zoom: 'Zoom',
  reset: 'Reset',
  'fullscreen-enter': 'Fullscreen',
};

// Placement sources from the fiber editor contract; fit is the editor's own
// default fit, not a visitor's edit.
const EDIT_SOURCES = {
  drag: 'Move',
  resize: 'Resize',
  rotate: 'Rotate',
  undo: 'Undo',
  reset: 'Reset',
};

// Fine steps so a tool taller than the screen still reports as it scrolls.
const VIEW_THRESHOLDS = Array.from({ length: 21 }, (_, i) => i / 20);

// Half the tool on screen, or the tool filling half the screen; a tool more
// than twice the screen's height can never meet the first.
const isHalfSeen = entry => {
  if (!entry.isIntersecting) return false;
  if (entry.intersectionRatio >= 0.5) return true;
  const screenHeight = entry.rootBounds?.height || window.innerHeight;
  return entry.intersectionRect.height >= screenHeight / 2;
};

const trackers = new Map();

// One tracker per tool per page, so every caller shares the same dedupe and
// engaged-time clock. Tool actions go out as analytics:tool (eVar35,
// event35); Tool View and engaged seconds carry the tool name in eVar36 so
// they never count as tool actions.
export const createToolAnalytics = toolName => {
  if (!trackers.has(toolName)) {
    trackers.set(toolName, new ToolAnalytics(toolName));
  }
  return trackers.get(toolName);
};

class ToolAnalytics {
  constructor(toolName) {
    this.toolName = toolName;
    this.reset();

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flush();
    });
    window.addEventListener('pagehide', () => this.flush());
    // A bfcache restore is a new page view with the same listeners.
    window.addEventListener('pageshow', event => {
      if (event.persisted) this.reset();
    });
  }

  reset() {
    this.hasBeenViewed = false;
    this.sentOnce = new Set();
    this.lastActivityAt = null;
    this.unreportedSeconds = 0;
    clearTimeout(this.idleTimer);
  }

  // Tool View fires once the tool is half seen (isHalfSeen). Pointer and key
  // input inside the element also feed the engaged-time clock; wheel doesn't,
  // since it is mostly the page scrolling past.
  observe(element) {
    if (!element) return;
    this.listenForActivity(element);

    if (!('IntersectionObserver' in window)) {
      this.viewed();
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        if (!entries.some(isHalfSeen)) return;
        observer.disconnect();
        this.viewed();
      },
      { threshold: VIEW_THRESHOLDS }
    );
    observer.observe(element);
  }

  viewed() {
    if (this.hasBeenViewed) return;
    this.hasBeenViewed = true;
    fireAdobeAnalyticsEvent({
      type: 'tool:view',
      detail: { eVar36: this.toolName },
    });
  }

  // A facet alone (Color) when the choice itself isn't reported.
  select(facet, value) {
    this.sendTool(
      value === undefined ? `Select|${facet}` : `Select|${facet}-${value}`
    );
  }

  // Views and edits count once per kind per page view, so one long orbit or
  // a run of nudges is one action.
  view(kind) {
    this.sendOnce(`View|${kind}`);
  }

  edit(kind) {
    this.sendOnce(`Edit|${kind}`);
  }

  upload(kind) {
    this.sendTool(`Upload|${kind}`);
  }

  // CTAs: Request a Quote, Contact a Rep, Share, Save Image, Add to Cart.
  action(name, label = '') {
    this.sendTool(label ? `${name}|${label}` : name);
  }

  viewChanged({ action } = {}) {
    if (VIEW_ACTIONS[action]) this.view(VIEW_ACTIONS[action]);
    else this.activity();
  }

  // Only the end of a gesture counts; the editor also reports every frame.
  placementChanged({ source, final } = {}) {
    if (!final) return;
    if (EDIT_SOURCES[source]) this.edit(EDIT_SOURCES[source]);
  }

  sendOnce(path) {
    this.activity();
    if (this.sentOnce.has(path)) return;
    this.sentOnce.add(path);
    this.fireTool(path);
  }

  sendTool(path) {
    this.activity();
    this.fireTool(path);
  }

  fireTool(path) {
    fireAdobeAnalyticsEvent({
      type: 'tool',
      detail: { eVar35: `${this.toolName}|${path}` },
    });
  }

  // Engaged time only accrues between interactions less than IDLE_SECONDS
  // apart, and is sent whenever the visitor goes idle or leaves the tab.
  activity() {
    const now = Date.now();
    if (this.lastActivityAt !== null) {
      const gap = (now - this.lastActivityAt) / 1000;
      if (gap <= IDLE_SECONDS) this.unreportedSeconds += gap;
    }
    this.lastActivityAt = now;

    clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => this.flush(), IDLE_SECONDS * 1000);
  }

  flush() {
    clearTimeout(this.idleTimer);
    this.lastActivityAt = null;
    const secondsEngaged = Math.floor(this.unreportedSeconds);
    this.unreportedSeconds -= secondsEngaged;
    if (secondsEngaged < 1) return;

    fireAdobeAnalyticsEvent({
      type: 'tool:time',
      detail: { eVar36: this.toolName, tool: { secondsEngaged } },
    });
  }

  listenForActivity(element) {
    let lastMoveAt = 0;
    const onActivity = () => this.activity();
    const onMove = event => {
      if (!event.buttons) return;
      const now = Date.now();
      if (now - lastMoveAt < MOVE_THROTTLE_MS) return;
      lastMoveAt = now;
      this.activity();
    };
    const passive = { passive: true };
    element.addEventListener('pointerdown', onActivity, passive);
    element.addEventListener('pointermove', onMove, passive);
    element.addEventListener('keydown', onActivity, passive);
  }
}
